import {
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client';
import {
  ICreateEcommerceComparisonParams,
  ICreateEcommerceComparisonResponse,
  IEcommerceObjectListResponse,
  IGetEcommerceComparisonAdvanceMetricResponse,
  IGetEcommerceComparisonListResponse,
  IGetEcommerceComparisonPopularityResponse,
  IGetEcommerceComparisonPositioningResponse,
  IGetEcommerceComparisonSentimentAnalysisResponse,
  IGetEcommerceStreamResponse,
  IManageVisibilityEcommerceStreamParams,
  IManageVisibilityEcommerceStreamResponse,
  IResponseEcommerceGetAvgRating,
  IResponseEcommerceGetChronologicalPerformance,
  IResponseEcommerceGetChronologicalRating,
  IResponseEcommerceGetChronologicalReviews,
  IResponseEcommerceGetComparisonChronologicalRating,
  IResponseEcommerceGetSentimentAnalysis,
  IResponseEcommerceGetTopPeople,
  IResponseEcommerceGetTotalComment,
  IResponseEcommerceGetTotalNegative,
  IResponseEcommerceGetTotalPositive,
  IResponseEcommerceGetWordCloud
} from 'src/models/ecommerce';
import {
  ECOMMERCE_GET_AVG_RATING,
  ECOMMERCE_GET_CHRONOLOGICAL_PERFORMANCE,
  ECOMMERCE_GET_CHRONOLOGICAL_RATING,
  ECOMMERCE_GET_CHRONOLOGICAL_REVIEWS,
  ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC,
  ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING,
  ECOMMERCE_GET_COMPARISON_LIST,
  ECOMMERCE_GET_COMPARISON_POPULARITY,
  ECOMMERCE_GET_COMPARISON_POSITIONING,
  ECOMMERCE_GET_COMPARISON_SENTIMENT,
  ECOMMERCE_GET_IGNORE_STREAM,
  ECOMMERCE_GET_OBJECTS,
  ECOMMERCE_GET_SENTIMENT_ANALYSIS,
  ECOMMERCE_GET_STREAM,
  ECOMMERCE_GET_TOP_PEOPLE,
  ECOMMERCE_GET_TOTAL_COMMENT,
  ECOMMERCE_GET_TOTAL_NEGATIVE,
  ECOMMERCE_GET_TOTAL_POSITIVE,
  ECOMMERCE_GET_WORD_CLOUD
} from './graphql/ecommerce/query';
import {
  ECOMMERCE_ADD_COMPARISON,
  MANAGE_VISIBILITY_ON_ECOMMERE_STREAM
} from './graphql/ecommerce/mutation';
import { IAdvanceSearchSocialMedia } from 'src/models/general';
import { useAppDispatch } from 'src/app/hooks';
import {
  reducerUpdateIgnoreStreamDataEcommerce,
  reducerUpdateStreamDataEcommerce
} from 'src/features/ecommerceStream';
import { OBJECT_MANAGEMENT_GET_GROUP_LIST } from './graphql/objectManagement/query';
import { IGetGroupObjectResponse } from './useObjectManagement';
import { IGroupObjectPayload } from 'src/models/objectManagement';
import { reducerUpdateEcommerceObjectList } from 'src/features/objectManagement';

const useEcommerce = () => {
  const defaultOptions: LazyQueryHookOptions = {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
  };

  const dispatch = useAppDispatch();

  const useGetTotalComment = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalComment,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalComment>(
      ECOMMERCE_GET_TOTAL_COMMENT,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetAvgRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetAvgRating,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetAvgRating>(
      ECOMMERCE_GET_AVG_RATING,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTotalPositive = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalPositive,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalPositive>(
      ECOMMERCE_GET_TOTAL_POSITIVE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTotalNegative = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTotalNegative,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTotalNegative>(
      ECOMMERCE_GET_TOTAL_NEGATIVE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetChronologicalPerformance = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetChronologicalPerformance,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetChronologicalPerformance>(
      ECOMMERCE_GET_CHRONOLOGICAL_PERFORMANCE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetChronologicalRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetChronologicalRating,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetChronologicalRating>(
      ECOMMERCE_GET_CHRONOLOGICAL_RATING,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetChronologicalReviews = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetChronologicalReviews,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetChronologicalReviews>(
      ECOMMERCE_GET_CHRONOLOGICAL_REVIEWS,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetSentimentAnalysis = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetSentimentAnalysis,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetSentimentAnalysis>(
      ECOMMERCE_GET_SENTIMENT_ANALYSIS,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetWordCloud = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetWordCloud,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetWordCloud>(
      ECOMMERCE_GET_WORD_CLOUD,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetTopPeople = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetTopPeople,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IResponseEcommerceGetTopPeople>(
      ECOMMERCE_GET_TOP_PEOPLE,
      {
        ...defaultOptions,
        ...options
      }
    );
  };

  const useGetEcommerceComparisonList = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonListResponse,
      { projectId: string }
    >
  ) => {
    return useLazyQuery<
      IGetEcommerceComparisonListResponse,
      { projectId: string }
    >(ECOMMERCE_GET_COMPARISON_LIST, options);
  };

  const useCreateEcommerceComparison = (
    options?: MutationHookOptions<
      ICreateEcommerceComparisonResponse,
      ICreateEcommerceComparisonParams
    >
  ) =>
    useMutation<
      ICreateEcommerceComparisonResponse,
      ICreateEcommerceComparisonParams
    >(ECOMMERCE_ADD_COMPARISON, options);

  const useGetComparisonPositioning = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonPositioningResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_POSITIONING, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonChronologicalRating = (
    options?: LazyQueryHookOptions<
      IResponseEcommerceGetComparisonChronologicalRating,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonPopularity = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonPopularityResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_POPULARITY, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonSentimentAnalysis = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonSentimentAnalysisResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_SENTIMENT, {
      ...defaultOptions,
      ...options
    });

  const useGetComparisonAdvanceMetric = (
    options?: LazyQueryHookOptions<
      IGetEcommerceComparisonAdvanceMetricResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC, {
      ...defaultOptions,
      ...options
    });

  const useGetStream = (
    options?: LazyQueryHookOptions<
      IGetEcommerceStreamResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_STREAM, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(
          reducerUpdateStreamDataEcommerce(res?.ecommerce_getStreamEcommerce)
        );
      }
    });

  const useGetIgnoreStream = (
    options?: LazyQueryHookOptions<
      IGetEcommerceStreamResponse,
      IAdvanceSearchSocialMedia
    >
  ) =>
    useLazyQuery(ECOMMERCE_GET_IGNORE_STREAM, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(
          reducerUpdateIgnoreStreamDataEcommerce(
            res?.ecommerce_getIgnoredStreamEcommerce
          )
        );
      }
    });

  const [ignorePost, responseIgnorePost] = useMutation<
    IManageVisibilityEcommerceStreamResponse,
    IManageVisibilityEcommerceStreamParams
  >(MANAGE_VISIBILITY_ON_ECOMMERE_STREAM);

  const [restorePost, responseRestorePost] = useMutation<
    IManageVisibilityEcommerceStreamResponse,
    IManageVisibilityEcommerceStreamParams
  >(MANAGE_VISIBILITY_ON_ECOMMERE_STREAM);

  const useGetEcommerceObjectList = (
    options?: LazyQueryHookOptions<
      IEcommerceObjectListResponse,
      OperationVariables
    >
  ) => {
    return useLazyQuery<IEcommerceObjectListResponse>(ECOMMERCE_GET_OBJECTS, {
      ...defaultOptions,
      ...options,
      onCompleted: (res) => {
        dispatch(reducerUpdateEcommerceObjectList(res?.ecommerce_getObject));
      }
    });
  };

  const useGetEcommerceGroups = (
    options?: QueryHookOptions<IGetGroupObjectResponse, IGroupObjectPayload>
  ) =>
    useQuery<IGetGroupObjectResponse, IGroupObjectPayload>(
      OBJECT_MANAGEMENT_GET_GROUP_LIST,
      options
    );

  return {
    useGetTotalComment,
    useGetAvgRating,
    useGetTotalPositive,
    useGetTotalNegative,
    useGetChronologicalPerformance,
    useGetChronologicalRating,
    useGetChronologicalReviews,
    useGetSentimentAnalysis,
    useGetWordCloud,
    useGetTopPeople,
    useGetEcommerceComparisonList,
    useCreateEcommerceComparison,
    useGetComparisonPositioning,
    useGetComparisonChronologicalRating,
    useGetComparisonPopularity,
    useGetComparisonSentimentAnalysis,
    useGetComparisonAdvanceMetric,
    useGetStream,
    useGetIgnoreStream,
    ignorePost,
    responseIgnorePost,
    restorePost,
    responseRestorePost,
    useGetEcommerceObjectList,
    useGetEcommerceGroups
  };
};

export default useEcommerce;
